import {
  isMultileg,
  type Market,
  type MarketAccount,
  type MarketCredentialWithConnectionStatus,
  type Security,
  type UnifiedLiquidityResponse,
} from '@talos/kyoko';
import { getMarketDataMarketSelectorItems } from './MarketData/useMarketDataMarketSelectorItems';
import { resolveAggregationMarketSelectorValue } from './presets/resolveAggregationMarketSelectorValue';
import type { MarketSelectorItem, MarketSelectorPreset } from './types';

/**
 * Context needed to build market-selector items for a trading symbol (Deep Dive, Market Data Card, etc.).
 * New Order / RFQ use {@link OrderMarketsField} with a {@link MarketSelectorValue} shape instead of flat panel fields.
 */
export type TradingSymbolMarketDataContext = {
  securitiesBySymbol: Map<string, Security>;
  marketAccountsByMarket: Map<string, MarketAccount[]>;
  marketsByName: Map<string, Market>;
  marketAccountsByName: Map<string, MarketAccount>;
  unifiedLiquidityTokenBySymbol: Map<string, UnifiedLiquidityResponse> | undefined;
  enableAccountSegregationWithRestrictedTrading: boolean;
  permittedMarketAccountNames: Set<string> | null;
  marketCredentialsWithConnectionStatusByMarketAccountName: Map<string, MarketCredentialWithConnectionStatus>;
};

export type TradingSymbolPanelMarkets = {
  symbol: string;
  aggregation: string | undefined;
  marketAccounts: string[];
  markets: string[];
};

/** Same defaults as Market Data Card / Deep Dive: no MSS query; optional UL markets. */
export function getTradingSymbolMarketSelectorItems(
  security: Security | undefined,
  symbolForUlCache: string,
  includeUnifiedLiquidityMarkets: boolean,
  ctx: TradingSymbolMarketDataContext
): MarketSelectorItem[] {
  const ulResponse = ctx.unifiedLiquidityTokenBySymbol?.get(symbolForUlCache);
  return getMarketDataMarketSelectorItems({
    security,
    securitiesBySymbol: ctx.securitiesBySymbol,
    includeUnifiedLiquidityMarkets,
    ulResponse,
    marketAccountsByMarket: ctx.marketAccountsByMarket,
    marketsByName: ctx.marketsByName,
    permittedMarketAccountNames: ctx.enableAccountSegregationWithRestrictedTrading
      ? ctx.permittedMarketAccountNames
      : null,
    enableMSSValidation: false,
    mssByAccountBySymbol: undefined,
    marketCredentialsWithConnectionStatusByMarketAccountName:
      ctx.marketCredentialsWithConnectionStatusByMarketAccountName,
  });
}

/** Maps combined selector names into layout-style `markets` vs `marketAccounts` (same as Market Data Card). */
export function splitMarketSelectorSelectionsToPanelFields(
  selections: string[],
  marketsByName: Map<string, Market>,
  marketAccountsByName: Map<string, MarketAccount>
): { markets: string[]; marketAccounts: string[] } {
  return {
    markets: selections.filter(name => marketsByName.has(name)),
    marketAccounts: selections.filter(name => marketAccountsByName.has(name)),
  };
}

/**
 * If the user's default aggregation applies to these items, returns panel fields; otherwise null.
 */
export function resolveDefaultAggregationToPanelFields(
  items: MarketSelectorItem[],
  useNewMarketSelector: boolean,
  defaultAggregationId: string | undefined,
  aggregationPresets: MarketSelectorPreset[] | undefined,
  marketsByName: Map<string, Market>,
  marketAccountsByName: Map<string, MarketAccount>
): { aggregation: string | undefined; markets: string[]; marketAccounts: string[] } | null {
  if (!useNewMarketSelector) {
    return null;
  }
  const resolved = resolveAggregationMarketSelectorValue({
    aggregationId: defaultAggregationId,
    aggregationPresets,
    items,
  });
  if (!resolved) {
    return null;
  }
  const { markets, marketAccounts } = splitMarketSelectorSelectionsToPanelFields(
    resolved.selections,
    marketsByName,
    marketAccountsByName
  );
  return {
    aggregation: resolved.type === 'preset' ? resolved.presetID : undefined,
    markets,
    marketAccounts,
  };
}

export function clearedTradingSymbolPanelMarkets(symbol: string): TradingSymbolPanelMarkets {
  return { symbol, aggregation: undefined, marketAccounts: [], markets: [] };
}

/** Inputs needed to derive default panel market fields for a trading symbol change. */
export type BuildTradingSymbolDefaultPanelMarketsParams = TradingSymbolMarketDataContext & {
  nextSymbol: string;
  security: Security | undefined;
  useNewMarketSelector: boolean;
  defaultAggregationId: string | undefined;
  aggregationPresets: MarketSelectorPreset[] | undefined;
  includeUnifiedLiquidityMarkets: boolean;
  marketSelectorItems?: MarketSelectorItem[];
};

/**
 * After a symbol change, applies the user's default aggregation when the new selector can resolve it.
 * Returns cleared panel fields when the symbol is unavailable, multileg, or has no applicable default.
 */
export function buildTradingSymbolDefaultPanelMarkets(
  params: BuildTradingSymbolDefaultPanelMarketsParams
): TradingSymbolPanelMarkets {
  const {
    nextSymbol,
    security,
    useNewMarketSelector,
    defaultAggregationId,
    aggregationPresets,
    includeUnifiedLiquidityMarkets,
    securitiesBySymbol,
    marketAccountsByMarket,
    marketsByName,
    marketAccountsByName,
    unifiedLiquidityTokenBySymbol,
    enableAccountSegregationWithRestrictedTrading,
    permittedMarketAccountNames,
    marketCredentialsWithConnectionStatusByMarketAccountName,
    marketSelectorItems,
  } = params;

  const cleared = clearedTradingSymbolPanelMarkets(nextSymbol);
  if (!security || isMultileg(security)) {
    return cleared;
  }

  const items =
    marketSelectorItems ??
    getTradingSymbolMarketSelectorItems(security, nextSymbol, includeUnifiedLiquidityMarkets, {
      securitiesBySymbol,
      marketAccountsByMarket,
      marketsByName,
      marketAccountsByName,
      unifiedLiquidityTokenBySymbol,
      enableAccountSegregationWithRestrictedTrading,
      permittedMarketAccountNames,
      marketCredentialsWithConnectionStatusByMarketAccountName,
    });

  const fromDefault = resolveDefaultAggregationToPanelFields(
    items,
    useNewMarketSelector,
    defaultAggregationId,
    aggregationPresets,
    marketsByName,
    marketAccountsByName
  );

  if (fromDefault) {
    return {
      symbol: nextSymbol,
      aggregation: fromDefault.aggregation,
      markets: fromDefault.markets,
      marketAccounts: fromDefault.marketAccounts,
    };
  }

  return cleared;
}
